import React from 'react'
import Head from 'next/head'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import striptagsFromObject from '@scentregroup/shared/helpers/striptags-from-object'
import { Thing } from 'schema-dts'

export interface IMetaDataProps {
  title?: string
  description?: string
  canonical?: string
  canonicalParams?: string[]
  canonicalDefaults?: Record<string, string | number>
  openGraph?: {
    title?: string
    description?: string
    image?: string
  }
  structuredData?: Exclude<Thing, string>
  showRobotsMetaTag?: boolean
}
interface IPageMetaDataProps {
  getMetaData: () => IMetaDataProps
}

function safeMeta<T = any>(value: T): T | undefined {
  return typeof value === 'string' && value === '' ? undefined : value
}

const isValidMeta = (meta: IMetaDataProps): any =>
  safeMeta(meta.title) ||
  safeMeta(meta.description) ||
  safeMeta(meta.openGraph?.title) ||
  safeMeta(meta.openGraph?.description) ||
  safeMeta(meta.openGraph?.image) ||
  meta.structuredData

const openGraphImageUrl = (url?: string): string => {
  if (!url) {
    return ''
  }
  const URL_PREFIX = 'https://res.cloudinary.com/scentre-group-au/image/fetch/'
  return `${URL_PREFIX}/c_fit,q_80,w_1024/${url}`
}

const PageMetaData: React.FunctionComponent<
  React.PropsWithChildren<IPageMetaDataProps>
> = props => {
  const { getMetaData } = props
  const meta = getMetaData()

  const structuredData = JSON.stringify(
    striptagsFromObject({
      '@context': 'https://schema.org/',
      ...(meta.structuredData ? meta.structuredData : {}),
    })
  )

  const canonical = safeMeta(meta?.canonical)
  const title = safeMeta(meta?.title)
  const description = safeMeta(meta?.description)

  const openGraphTitle = safeMeta(meta.openGraph?.title) ?? title
  const openGraphDescription =
    safeMeta(meta.openGraph?.description) ?? description
  const openGraphImage =
    safeMeta(meta.openGraph?.image) && openGraphImageUrl(meta.openGraph?.image)

  let twitterCard: string | undefined
  if (openGraphImage) {
    twitterCard = 'summary_large_image'
  } else if (openGraphTitle || openGraphDescription) {
    twitterCard = 'summary'
  }

  const completeCanonical = canonical
    ? completelyCanonicalUrl(
        canonical,
        meta.canonicalParams,
        meta.canonicalDefaults
      )
    : undefined

  return isValidMeta(meta) ? (
    <Head>
      {title && <title>{title}</title>}
      {description && (
        <meta key="description" name="description" content={description} />
      )}
      {twitterCard && (
        <meta key="twitter:card" name="twitter:card" content={twitterCard} />
      )}
      {openGraphTitle && (
        <meta key="og:title" property="og:title" content={openGraphTitle} />
      )}
      {openGraphDescription && (
        <meta
          key="og:description"
          property="og:description"
          content={openGraphDescription}
        />
      )}
      {openGraphImage && (
        <meta key="og:image" property="og:image" content={openGraphImage} />
      )}
      {completeCanonical && (
        <>
          <meta key="og:url" property="og:url" content={completeCanonical} />
          <link key="canonical" rel="canonical" href={completeCanonical} />
        </>
      )}

      {meta.structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: structuredData }}
        />
      )}
      {meta.showRobotsMetaTag && (
        <meta name="robots" content="max-image-preview:large" />
      )}
    </Head>
  ) : null
}
export default PageMetaData
